import { Icon } from '@vrplatform/icons';
import {
  Box,
  PasswordInput,
  type PasswordInputProps,
  Popover,
  Progress,
  Text,
  rem,
} from '@mantine/core';
import { type ChangeEventHandler, useState } from 'react';

const minPasswordLength = 8;

type InputPasswordProps = {
  withPopover?: boolean;
  name: string;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

export const InputPassword = ({
  withPopover = true,
  value = '',
  onChange,
  name,
  ...props
}: InputPasswordProps & PasswordInputProps) => {
  const [requirementsOpen, setRequirementsOpen] = useState(false);

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.message}
      meets={requirement.value.test(value)}
    />
  ));

  const strength = getStrength(value);
  const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red';

  const inputPadding = `${rem(16)} ${rem(12)}`;

  return (
    <Popover
      opened={withPopover ? requirementsOpen : false}
      position="bottom-start"
      radius={'sm'}
      withRoles={false}
      withArrow
      withinPortal
      styles={{
        dropdown: { maxWidth: '100%', width: 360 },
      }}
      trapFocus={false}
      transitionProps={{ transition: 'pop-top-left' }}
      width="target"
    >
      <Popover.Target>
        <PasswordInput
          aria-label={
            typeof props.label === 'string'
              ? props.label
              : props.placeholder || name
          }
          radius={'sm'}
          id={name}
          value={value}
          name={name}
          onChange={onChange}
          onFocusCapture={() => setRequirementsOpen(true)}
          onBlurCapture={() => setRequirementsOpen(false)}
          styles={(theme) => ({
            innerInput: {
              padding: inputPadding,
            },
            input: {
              padding: inputPadding,
              fontSize: '13px',
              borderColor: props.error ? theme.colors.red[4] : undefined,
              '& ::placeholder, & ::-webkit-input-placeholder': {
                color: '#BEC0C9',
                fontSize: 13,
                fontWeight: 500,
              },
              '&:focus-within': {
                boxShadow: `0px 0px 0px 2px ${
                  theme.colors[theme.primaryColor][4]
                }40`,
                borderColor: props.error
                  ? theme.colors.red[6]
                  : theme.colors[theme.primaryColor][6],
              },
            },
          })}
          {...props}
          error={undefined}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <Progress
          color={color}
          value={strength}
          size={5}
          style={{ marginBottom: 10 }}
        />
        <PasswordRequirement
          label={`Includes at least ${minPasswordLength} characters`}
          meets={value.length > minPasswordLength - 1}
        />
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
};

function PasswordRequirement({
  meets,
  label,
}: {
  meets: boolean;
  label: string;
}) {
  return (
    <Text
      c={meets ? 'teal' : 'red'}
      sx={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
      component="div"
    >
      {meets ? (
        <Icon
          icon={'CheckIcon'}
          size={18}
          color={(theme) => theme.colors.teal[6]}
        />
      ) : (
        <Icon
          icon={'CrossIcon'}
          size={18}
          color={(theme) => theme.colors.red[6]}
        />
      )}{' '}
      <Box ml={10}>{label}</Box>
    </Text>
  );
}

const requirements = [
  { value: /[0-9]/, message: 'Includes number' },
  { value: /[a-z]/, message: 'Includes lowercase letter' },
  { value: /[A-Z]/, message: 'Includes uppercase letter' },
  {
    value: /[!@#$%^&*()_+\-?]/,
    message: 'Includes special symbol (!@#$%^&*()_+-?)',
  },
];
export const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-?])[A-Za-z\d!@#$%^&*()_+\-?]{8,}$/;

function getStrength(password: string) {
  let multiplier = password.length > minPasswordLength - 1 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.value.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}
